import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiTruck, FiDollarSign, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import api from '../services/api';
import DataTable from '../components/common/DataTable';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, shipmentsRes, invoicesRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/truck?limit=5'),
        api.get('/accounting/invoices?limit=5'),
      ]);

      setStats(statsRes.data);
      setShipments(shipmentsRes.data.data || shipmentsRes.data || []);
      setInvoices(invoicesRes.data.data || invoicesRes.data || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const statusDistribution = stats?.shipmentsByStatus || {};
  const statusLabels = Object.keys(statusDistribution);
  const statusValues = Object.values(statusDistribution);

  const chartData = {
    labels: statusLabels.map(s => s.charAt(0).toUpperCase() + s.slice(1)),
    datasets: [{
      label: 'Shipments by Status',
      data: statusValues,
      backgroundColor: [
        '#3498db',
        '#f39c12',
        '#27ae60',
        '#e74c3c',
        '#95a5a6',
      ],
      borderColor: '#fff',
      borderWidth: 2,
    }],
  };

  const shipmentColumns = [
    { key: 'file_no', label: 'File No', sortable: true },
    { key: 'shipment_type', label: 'Type', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
    { key: 'shipper_name', label: 'Shipper', sortable: true },
    { key: 'origin', label: 'Origin', sortable: true },
    { key: 'destination', label: 'Destination', sortable: true },
  ];

  const invoiceColumns = [
    { key: 'invoice_no', label: 'Invoice No', sortable: true },
    { key: 'invoice_type', label: 'Type', sortable: true },
    { key: 'amount', label: 'Amount', sortable: true, render: (v) => `$${(v || 0).toFixed(2)}` },
    { key: 'status', label: 'Status', sortable: true, type: 'status' },
    { key: 'issue_date', label: 'Issue Date', sortable: true, render: (v) => v ? new Date(v).toLocaleDateString() : '-' },
  ];

  return (
    <div>
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card" style={{ position: 'relative' }}>
          <div className="summary-card-icon"><FiTruck /></div>
          <div className="summary-card-label">Total Shipments</div>
          <div className="summary-card-value">{stats?.totalShipments || 0}</div>
        </div>

        <div className="summary-card success" style={{ position: 'relative' }}>
          <div className="summary-card-icon"><FiCheckCircle /></div>
          <div className="summary-card-label">Delivered</div>
          <div className="summary-card-value">{stats?.deliveredShipments || 0}</div>
        </div>

        <div className="summary-card warning" style={{ position: 'relative' }}>
          <div className="summary-card-icon"><FiAlertCircle /></div>
          <div className="summary-card-label">In Transit</div>
          <div className="summary-card-value">{stats?.inTransitShipments || 0}</div>
        </div>

        <div className="summary-card danger" style={{ position: 'relative' }}>
          <div className="summary-card-icon"><FiDollarSign /></div>
          <div className="summary-card-label">Pending Payment</div>
          <div className="summary-card-value">${(stats?.pendingPayment || 0).toFixed(2)}</div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="summary-cards">
        <div className="summary-card" style={{ position: 'relative' }}>
          <div className="summary-card-icon"><FiDollarSign /></div>
          <div className="summary-card-label">Total AR</div>
          <div className="summary-card-value">${(stats?.totalAR || 0).toFixed(2)}</div>
        </div>

        <div className="summary-card" style={{ position: 'relative' }}>
          <div className="summary-card-icon"><FiDollarSign /></div>
          <div className="summary-card-label">Total AP</div>
          <div className="summary-card-value">${(stats?.totalAP || 0).toFixed(2)}</div>
        </div>

        <div className="summary-card danger" style={{ position: 'relative' }}>
          <div className="summary-card-icon"><FiAlertCircle /></div>
          <div className="summary-card-label">Outstanding</div>
          <div className="summary-card-value">${(stats?.outstanding || 0).toFixed(2)}</div>
        </div>

        <div className="summary-card" style={{ position: 'relative' }}>
          <div className="summary-card-icon"><FiCheckCircle /></div>
          <div className="summary-card-label">Total Paid</div>
          <div className="summary-card-value">${(stats?.totalPaid || 0).toFixed(2)}</div>
        </div>
      </div>

      {/* Charts and Recent Data */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        {statusLabels.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Shipment Status Distribution</h3>
            </div>
            <div style={{ height: '300px', position: 'relative' }}>
              <Doughnut
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Recent Shipments */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Recent Shipments</h3>
        </div>
        <DataTable
          columns={shipmentColumns}
          data={shipments}
          loading={loading}
          pageSize={5}
        />
      </div>

      {/* Recent Invoices */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Recent Invoices</h3>
        </div>
        <DataTable
          columns={invoiceColumns}
          data={invoices}
          loading={loading}
          pageSize={5}
        />
      </div>
    </div>
  );
};

export default Dashboard;
